import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { client } from '../api/client';

interface DailyStat { date: string; total: number; correct: number; topics: number; }
interface TopicMastery { id: number; name: string; name_cn: string; subject_name: string; attempts: number; correct: number; mastery: number; }

export function ProgressPage() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    client.api.fetch(`/api/public/learning-stats?user_id=${user.id}&period=${period}`)
      .then(r => r.json())
      .then(d => { setStats(d.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user, period]);

  if (!user) return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-slate-600">请先登录</p></div>;
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  const dailyStats: DailyStat[] = stats?.dailyStats || [];
  const topicMastery: TopicMastery[] = stats?.topicMastery || [];
  const maxDaily = Math.max(...dailyStats.map(d => d.total), 1);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">📊 学习进度</h1>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setPeriod(d)}
              className={`px-3 py-1.5 rounded-lg text-sm ${period === d ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {d}天
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">总练习</p>
          <p className="text-2xl font-bold text-slate-800">{stats?.totalPractices || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">正确数</p>
          <p className="text-2xl font-bold text-emerald-600">{stats?.totalCorrect || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">正确率</p>
          <p className="text-2xl font-bold text-blue-600">{stats?.overallAccuracy || 0}%</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-500">连续学习</p>
          <p className="text-2xl font-bold text-orange-500">{stats?.streak || 0} 天</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <h2 className="font-semibold text-slate-800 mb-4">每日练习量</h2>
        {dailyStats.length === 0 ? (
          <p className="text-slate-400 text-center py-8">暂无练习记录，开始做题吧！</p>
        ) : (
          <div className="flex items-end gap-1 h-40">
            {dailyStats.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t" style={{ height: `${(d.total / maxDaily) * 120}px` }} title={`${d.date}: ${d.total}题, ${d.correct}对`} />
                </div>
                <span className="text-[10px] text-slate-400 rotate-[-45deg]">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="font-semibold text-slate-800 mb-4">知识点掌握度</h2>
        {topicMastery.length === 0 ? (
          <p className="text-slate-400 text-center py-8">暂无数据</p>
        ) : (
          <div className="space-y-3">
            {topicMastery.map((t, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-40 truncate text-sm text-slate-700">{t.name_cn || t.name}</div>
                <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${t.mastery >= 80 ? 'bg-emerald-500' : t.mastery >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${t.mastery}%` }} />
                </div>
                <span className="text-sm font-medium w-12 text-right">{t.mastery}%</span>
                <span className="text-xs text-slate-400 w-16">{t.attempts}次</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
